import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { settingsService } from '../../services/dataService';
import { downloadBlob } from '../../utils/csv';
import { KpiCard, percent, StatusPill } from '../../components/DetailDashboard';
import dashboardStyles from '../assets/css/DetailDashboard.module.css';
import styles from './SettingsErrorLogsPage.module.css';

const PAGE_SIZE = 50;
const toIso = (value) => (value ? new Date(value).toISOString() : undefined);

// tone is a StatusPill value that maps to the wanted colour.
const OUTCOMES = {
  verified: { label: 'Verified', tone: 'completed' },
  not_verified: { label: 'Not verified', tone: 'pending' },
  error: { label: 'Failed', tone: 'failed' },
};

const ENDPOINTS = [
  '/sha/v2/verification',
  '/sha/v1/claim-verification',
  '/sha/v1/verification',
  '/sha/verification',
];

const EMPTY_FILTERS = { outcome: '', endpoint: '', search: '', user: '', startTime: '', endTime: '' };

const identifiers = (entry) => [
  ['Claim', entry.claim_id],
  ['Request', entry.emr_request_id],
  ['Internal', entry.internal_request_id],
  ['Patient', entry.patient_id],
  ['Facility', entry.facility_id],
].filter(([, value]) => value);

const apiFilters = (filters) => ({
  outcome: filters.outcome || undefined,
  endpoint: filters.endpoint || undefined,
  search: filters.search || undefined,
  user: filters.user || undefined,
  start_time: toIso(filters.startTime),
  end_time: toIso(filters.endTime),
});

const SettingsShaVerificationLogsPage = () => {
  const [data, setData] = useState({ total: 0, items: [], summary: null });
  const [page, setPage] = useState(1);
  const [form, setForm] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [exporting, setExporting] = useState(false);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await settingsService.getShaVerificationLogs({
        ...apiFilters(filters),
        page,
        page_size: PAGE_SIZE,
      }));
    } catch (err) {
      setError(err?.detail || 'Failed to load SHA verification calls');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const setField = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const applyFilters = (event) => {
    event.preventDefault();
    if (form.startTime && form.endTime && new Date(form.endTime) < new Date(form.startTime)) {
      setError('End time must be on or after start time');
      return;
    }
    setPage(1);
    setFilters({ ...form, search: form.search.trim(), user: form.user.trim() });
  };

  const clearFilters = () => {
    setForm(EMPTY_FILTERS);
    setPage(1);
    setFilters(EMPTY_FILTERS);
  };

  // Exports the applied filters (what the table shows), across all pages.
  const exportCsv = async () => {
    setExporting(true);
    setError('');
    try {
      const blob = await settingsService.exportShaVerificationLogs(apiFilters(filters));
      const stamp = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `sha-verification-calls_${stamp}.csv`);
    } catch (err) {
      setError(err?.detail || 'Failed to export SHA verification calls');
    } finally {
      setExporting(false);
    }
  };

  const { summary } = data;
  const pageCount = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1>SHA Verification Calls</h1>
          <p>Every call to the SHA verification API, whether it verified, found nothing or failed.</p>
        </div>
        <Link className={styles.backLink} to="/settings">Back to Settings</Link>
      </header>

      {summary ? (
        <div className={dashboardStyles.kpiGrid}>
          <KpiCard label="Calls" value={summary.total} hint="For these filters" />
          <KpiCard label="Verified" value={summary.verified} hint={`${percent(summary.verified, summary.total)}% of calls`} />
          <KpiCard label="Not verified" value={summary.not_verified} hint={`${percent(summary.not_verified, summary.total)}% of calls`} />
          <KpiCard label="Failed" value={summary.error} hint={`${percent(summary.error, summary.total)}% of calls`} />
        </div>
      ) : null}

      <section className={styles.panel}>
        <form className={styles.filters} onSubmit={applyFilters}>
          <label>
            Outcome
            <select value={form.outcome} onChange={setField('outcome')}>
              <option value="">All outcomes</option>
              {Object.entries(OUTCOMES).map(([value, { label }]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label>
            Endpoint
            <select value={form.endpoint} onChange={setField('endpoint')}>
              <option value="">All endpoints</option>
              {ENDPOINTS.map((endpoint) => <option key={endpoint} value={endpoint}>{endpoint}</option>)}
            </select>
          </label>
          <label>
            Claim / request / patient
            <input value={form.search} onChange={setField('search')} placeholder="Claim, request, patient or facility ID" />
          </label>
          <label>
            Caller
            <input value={form.user} onChange={setField('user')} placeholder="Username" />
          </label>
          <label>
            From
            <input type="datetime-local" value={form.startTime} onChange={setField('startTime')} />
          </label>
          <label>
            To
            <input type="datetime-local" value={form.endTime} onChange={setField('endTime')} />
          </label>
          <div className={styles.filterActions}>
            <button type="submit">Apply</button>
            <button type="button" className={styles.secondaryButton} onClick={clearFilters}>Clear</button>
            <button type="button" className={styles.secondaryButton} onClick={exportCsv} disabled={exporting || !data.total}>
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>
        </form>
      </section>

      {error ? <div className={styles.error}>{error}</div> : null}

      <section className={styles.panel}>
        <div className={styles.summary}>{loading ? 'Loading…' : `${data.total} call${data.total === 1 ? '' : 's'}`}</div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Time</th><th>Outcome</th><th>Endpoint</th><th>Identifiers</th><th>Caller</th>
                <th>Client IP</th><th>Took</th><th>Details</th>
              </tr>
            </thead>
            <tbody>
              {!loading && data.items.length === 0 ? (
                <tr><td colSpan="8" className={styles.empty}>No SHA verification calls match the filters.</td></tr>
              ) : data.items.map((entry) => {
                const outcome = OUTCOMES[entry.outcome] || { label: entry.outcome, tone: 'cancelled' };
                return (
                  <tr key={entry.id}>
                    <td className={styles.nowrap}>{new Date(entry.occurred_at).toLocaleString()}</td>
                    <td className={styles.nowrap}>
                      <StatusPill value={outcome.tone} label={outcome.label} />
                      <span className={dashboardStyles.subText}>HTTP {entry.status_code}</span>
                    </td>
                    <td className={styles.nowrap}>
                      <code>{entry.endpoint}</code>
                      {entry.source ? <span className={dashboardStyles.subText}>source: {entry.source}</span> : null}
                    </td>
                    <td>
                      {identifiers(entry).map(([label, value]) => (
                        <div key={label} className={styles.nowrap}>{label}: <code>{value}</code></div>
                      ))}
                      {identifiers(entry).length === 0 ? '—' : null}
                    </td>
                    <td>
                      {entry.user || '—'}
                      <span className={dashboardStyles.subText}>{entry.client_type || ''}</span>
                    </td>
                    <td className={styles.nowrap}>{entry.client_ip || '—'}</td>
                    <td className={styles.nowrap}>{entry.duration_ms == null ? '—' : `${entry.duration_ms} ms`}</td>
                    <td>
                      {entry.detail || '—'}
                      {entry.request_payload ? (
                        <details>
                          <summary>Received payload</summary>
                          <pre>{JSON.stringify(entry.request_payload, null, 2)}</pre>
                        </details>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className={styles.pagination}>
          <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>Previous</button>
          <span>Page {page} of {pageCount}</span>
          <button type="button" disabled={page >= pageCount || loading} onClick={() => setPage((value) => value + 1)}>Next</button>
        </div>
      </section>
    </div>
  );
};

export default SettingsShaVerificationLogsPage;
